'use client'

import { memo } from 'react'
import { splitDuration, type TrackerCopy, type TrackerLocale } from '@/lib/santa-tracker'
import { formatNumber } from './format'

/**
 * La cuenta atrás es el elemento visual principal de la página: cuatro cifras
 * grandes que se leen de un vistazo desde el sofá.
 *
 * Se aísla en su propio componente memoizado porque es lo único que cambia
 * cada segundo; así el resto del árbol (mapa, tarjetas, listas) no se
 * re-renderiza al ritmo del reloj.
 */

const CELL = 'flex min-w-[4.25rem] flex-col items-center rounded-2xl border border-white/15 bg-white/10 px-3 py-3 backdrop-blur-sm md:min-w-[6rem] md:px-5 md:py-4'
const COMPACT_CELL = 'flex min-w-[3.5rem] flex-col items-center px-1 py-0.5'

export interface TrackerCountdownProps {
  /**
   * Solo el bloque `countdown` de la copy, no la tabla completa del tracker:
   * quien monta el reloj desde otra superficie (la home) serializa como prop
   * únicamente las etiquetas que se pintan, no los ~15 KB de contenido del
   * tracker (misma lógica ADR-036: al cliente le llega solo lo que se renderiza).
   */
  countdown: TrackerCopy['countdown']
  locale: TrackerLocale
  heading: string
  remainingMs: number
  /** Cuando el trineo está sobrevolando tu zona, el reloj deja paso al aviso. */
  arriving: boolean
  /** Presentación ligera para cuentas atrás secundarias, como la de la home. */
  density?: 'default' | 'compact'
}

export const TrackerCountdown = memo(function TrackerCountdown({
  countdown,
  locale,
  heading,
  remainingMs,
  arriving,
  density = 'default',
}: TrackerCountdownProps) {
  const compact = density === 'compact'
  const parts = splitDuration(remainingMs)
  const cellClassName = compact ? COMPACT_CELL : CELL
  const headingClassName = compact
    ? 'text-sm font-medium text-white/75 md:text-base'
    : 'font-christmas text-lg text-white/85 md:text-xl'
  const numberClassName = compact
    ? 'text-2xl font-semibold tabular-nums text-white sm:text-3xl'
    : 'text-3xl font-bold tabular-nums text-white md:text-5xl'
  const unitLabelClassName = compact
    ? 'mt-0.5 text-[0.65rem] uppercase tracking-wide text-white/60'
    : 'mt-1 text-[0.7rem] uppercase tracking-wide text-white/65 md:text-xs'
  const units = [
    { value: parts.days, label: countdown.units.days },
    { value: parts.hours, label: countdown.units.hours },
    { value: parts.minutes, label: countdown.units.minutes },
    { value: parts.seconds, label: countdown.units.seconds },
  ]

  if (arriving) {
    return (
      <div className="rounded-3xl border border-amber-200/40 bg-amber-400/15 px-6 py-6 text-center">
        <p className="font-christmas text-3xl font-bold text-amber-100 md:text-4xl">
          {countdown.arrivingNow}
        </p>
      </div>
    )
  }

  return (
    <div
      className={
        compact
          ? 'flex w-full flex-col items-center gap-1.5'
          : 'flex flex-col items-center gap-3'
      }
      role="timer"
      aria-label={countdown.ariaLabel}
    >
      <p className={headingClassName}>{heading}</p>
      <div
        className={
          compact
            ? 'flex w-full flex-nowrap justify-center gap-1 sm:gap-2 md:gap-3'
            : 'flex flex-wrap justify-center gap-2 md:gap-3'
        }
      >
        {units.map((unit) => (
          <div key={unit.label} className={cellClassName}>
            <span className={numberClassName}>{formatNumber(unit.value, locale)}</span>
            <span className={unitLabelClassName}>{unit.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
})
